import { GlobalStateController } from "bai-react-global-state";
import type { Unsubscribe, User } from "firebase/auth";
import firebase from "firebase/compat/app";
import Cookies from "js-cookie";
import _ from "lodash";
import psl from "psl";

import { email as emailApi } from "apis-v2/callables";
import { getSequentialRunner, SequentialRunner } from "common/utils/runSequentially";
import sanitizeRelativeUrl from "common/utils/sanitizeRelativeUrl";
import Analytics from "js/analytics";
import type { PossibleAnswers } from "js/analytics/SignupFlowMay2026";
import Api from "js/core/api";
import getLogger, { LogGroup } from "js/core/logger";
import { getGoogleAuthTokens } from "js/core/oauth/getGoogleAuthTokens";
import { getMicrosoftAuthTokens } from "js/core/oauth/getMicrosoftAuthTokens";
import { initializeAllExperiments } from "js/core/services/experiments";
import { preserveGA4QueryParams } from "js/core/utilities/ga4QueryParams";
import { removeQueryParam } from "js/core/utilities/queryParams";
import db from "js/db";
import { ensureSignedIntoAllInstances, auth as fbAuth } from "js/firebase/auth";
import { app } from "js/namespaces";

const logger = getLogger(LogGroup.AUTH);

export type PageMode =
    | "signIn"
    | "createAccount"
    | "verifyEmail"
    | "ssoRedirect"
    | "resetPassword"
    | "waitForGoogle"
    | "waitForMicrosoft"
    | "tour"
    | "pricing";

const pageModes: PageMode[] = [
    "signIn",
    "createAccount",
    "verifyEmail",
    "ssoRedirect",
    "resetPassword",
    "waitForGoogle",
    "waitForMicrosoft",
    "tour",
    "pricing",
];

export type ActivePageStatus =
    | "loading"
    | "idle"
    | "submitting"
    | "success"
    | "error"
    | null;

export type Provider = "email" | "google" | "microsoft";

export type TourSurveyQuestion = { id: string, text: string };

type Digit = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export type VerificationCell = Digit | null;
export type VerificationCode = [VerificationCell, VerificationCell, VerificationCell, VerificationCell, VerificationCell, VerificationCell];

export interface AuthControllerState {
    initialized: boolean;
    currentUser: User | null;
    email: string;
    readonlyEmail: boolean;
    pageMode: PageMode;
    activePageStatus: ActivePageStatus;
    verificationCode: VerificationCode;
    tourPage: number;
    plan: string | null;
    interval: string | null;
    errorMessage: string;
    /**
     * Indicates if the user was created during the current session,
     * if they bailed, then we're ok with that (they'll land on the old
     * onboarding in case they skipped plan selection)
     */
    isNewUser: boolean;
    experiments: Record<string, { enabled: boolean }> | null;
}

const initialState: AuthControllerState = {
    initialized: false,
    currentUser: null,
    email: "",
    readonlyEmail: false,
    pageMode: "createAccount",
    activePageStatus: "loading",
    verificationCode: [null, null, null, null, null, null],
    tourPage: 1,
    plan: null,
    interval: null,
    errorMessage: "",
    isNewUser: false,
    experiments: null,
};

const loggerPrefix = "[AuthController]";

class AuthController extends GlobalStateController<AuthControllerState> {
    private _runSequentially: SequentialRunner;
    private _auth = null;
    private _unsubscribeAuthState: Unsubscribe | null = null;
    private _initializePromise: Promise<void> | null = null;
    private _registerPromise: Promise<Error | undefined> | null = null;
    private _registerPromiseResolve: (error?: Error) => void = () => { };

    private _disposed = false;

    /************************
     Init and Setup
     ************************/
    constructor() {
        super(_.cloneDeep(initialState));
        this._runSequentially = getSequentialRunner();
    }

    private _initialize() {
        const isGoogleAuthenticationRedirect = () => {
            const params = new URLSearchParams(window.location.search);
            return params.has("google_auth_success") || params.has("google_auth_error");
        };

        const isMicrosoftAuthenticationRedirect = () => {
            const params = new URLSearchParams(window.location.search);
            return params.has("microsoft_auth_success") || params.has("microsoft_auth_error");
        };

        return this._runSequentially(async () => {
            logger.info(`${loggerPrefix} initialize()`, {});

            this._auth = fbAuth();

            await this._updateState(_.cloneDeep(initialState));

            await new Promise<void>(resolve => {
                this._unsubscribeAuthState = this._auth.onAuthStateChanged(async user => {
                    try {
                        if (this._disposed) {
                            return;
                        }

                        logger.info(`${loggerPrefix} firebase auth state changed`);

                        Cookies.set("logged-in", `${!!user}`);

                        const currentUser = this._state.currentUser;

                        if (user && !currentUser) {
                            Analytics.util.identifyAmplitudeAndGtmOnly(user);
                            await ensureSignedIntoAllInstances()
                                .catch(err => logger.error(err, `${loggerPrefix} ensureSignedIntoAllInstances() failed`));
                            this._updateLastSignedInAt(user);
                        }

                        const params = new URLSearchParams(window.location.search);
                        const readonlyEmail = params.get("readonlyEmail") === "true";
                        const email = params.get("email");
                        // Persist new user status in case of refresh (mostly for Cypress tests)
                        const isNewUser = params.get("new") === "true";

                        await this._updateState({
                            ...(user ? { currentUser: user, email: user.email } : {}),
                            ...(!user && email ? { email, readonlyEmail } : {}),
                            isNewUser
                        });

                        if (this._registerPromise) {
                            const err = await this._registerPromise;
                            if (err) {
                                this.setErrorMessage(err.message);
                                return;
                            }
                        }

                        const setPageModeAndEnsureInitialized = async (pageMode: PageMode) => {
                            await this.setPageMode(pageMode);

                            const initialized = this._state.initialized;
                            if (!initialized) {
                                await this._updateState({ initialized: true, errorMessage: "" });
                            }
                        };

                        if (user) {
                            if (this._state.isNewUser && !this._state.experiments) {
                                await this._initializeAllExperiments();
                            }

                            if (!user.emailVerified) {
                                await setPageModeAndEnsureInitialized("verifyEmail");
                                return;
                            }

                            if (!this._state.isNewUser) {
                                window.location.href = this._getContinueUrl();
                                return;
                            }

                            const pageMode = new URLSearchParams(location.search).get("pageMode");
                            await setPageModeAndEnsureInitialized(pageMode === "pricing" ? "pricing" : "tour");

                            return;
                        }

                        let pageMode: PageMode = new URLSearchParams(location.search).get("pageMode") as PageMode;
                        if (!pageModes.includes(pageMode)) {
                            pageMode = null;
                        }
                        if (!pageMode) {
                            if (window.location.pathname === "/login") {
                                pageMode = "signIn";
                            } else {
                                pageMode = "createAccount";
                            }
                        }

                        await setPageModeAndEnsureInitialized(pageMode);

                        if (isGoogleAuthenticationRedirect()) {
                            await this._handleGoogleRedirectResult();
                        }

                        if (isMicrosoftAuthenticationRedirect()) {
                            await this._handleMicrosoftRedirectResult();
                        }
                    } catch (err) {
                        logger.error(err, `${loggerPrefix} initialize() failed to initialize`);

                        await this._updateState({
                            initialized: false,
                            errorMessage: err.message,
                            activePageStatus: "error",
                        });
                    } finally {
                        resolve();
                    }
                });
            });
        });
    }

    public get initialized() {
        return this._state.initialized;
    }

    public initialize() {
        if (!this._initializePromise) {
            this._initializePromise = this._initialize();
        }
        return this._initializePromise;
    }

    private _updateLastSignedInAt(user: User) {
        const userRef = db("users").child(user.uid);
        userRef.child("id").once("value")
            .then((snap: { exists: () => any; }) => snap.exists())
            .then((userRecordIdExists: any) => {
                if (userRecordIdExists) {
                    return userRef.child("lastSignedInAt").set(Date.now());
                }
            })
            .catch(err => logger.error(err, `${loggerPrefix} failed to update user model`));
    }

    /************************
     Auth - User creation + login
     ************************/

    private _partnerStackReferral = () => {
        const rawPsXid = Cookies.get("ps_xid");
        const rawPsPartnerKey = Cookies.get("ps_partner_key");

        const partnerStackClickId = typeof rawPsXid === "string" ? rawPsXid.substring(0, 255) : undefined;
        const partnerStackPartnerKey = typeof rawPsPartnerKey === "string" ? rawPsPartnerKey.substring(0, 255) : undefined;

        if (!partnerStackClickId || !partnerStackPartnerKey) {
            return {};
        }

        return { partnerStackClickId, partnerStackPartnerKey };
    }

    private _ensureUserRegistered = async ({ provider, user }: { provider: Provider, user: User }) => {
        logger.info(`${loggerPrefix} Calling registerUser() for provider ${provider}`);
        const referer = window.location.pathname + window.location.search + window.location.hash;
        const partnerStackReferral = this._partnerStackReferral();
        const affiliate = new URLSearchParams(window.location.search).get("affiliate");

        let isNew = await Api.registerUser.post({
            affiliate,
            provider,
            referer,
            ...partnerStackReferral
        });

        isNew = isNew || this._state.isNewUser;

        await this._updateState({ isNewUser: isNew });
        this._pushParamToUrl("new", isNew.toString());

        Analytics.util.trackState({ "is_signing_up": !!isNew });
        if (isNew) {
            Analytics.Auth.Signup({ email: user.email, uid: user.uid });
        } else {
            Analytics.Auth.Login({ email: user.email, uid: user.uid });
        }
        Analytics.SignupFlow.NavForward({ step_name: `May 2026 Signup`, step_number: 1 });
    }

    private _getSSOStatus = async (): Promise<"none" | "strict" | "optional"> => {
        const { email } = this._state;
        const domain = email.split("@").pop();

        const parsed = psl.parse(domain);
        if (parsed.sld && parsed.listed) {
            const { status } = await Api.ssoStatus.get({ email });
            return status;
        }

        return "none";
    }

    private async _withRegisterPromise<T>(action: () => Promise<T>): Promise<T> {
        this._registerPromise = new Promise(resolve => {
            // Resolving error to avoid unhandled promise rejection if nothing is awaiting the promise
            this._registerPromiseResolve = (error: Error | undefined) => resolve(error);
        });

        let error: Error | undefined;
        try {
            return await action();
        } catch (err) {
            error = err as Error;
            throw err;
        } finally {
            this._registerPromiseResolve(error);
            this._registerPromise = null;
            this._registerPromiseResolve = () => { };
        }
    }

    private _humanizeFirebaseError(err: Error & { code?: string }) {
        if (err.code === "auth/email-already-in-use") {
            return "Email already in use";
        }
        if (err.code === "auth/invalid-credential") {
            return "Invalid email or password";
        }
        if (err.code === "auth/invalid-email") {
            return "Invalid email";
        }
        if (err.code === "auth/wrong-password") {
            return "Wrong password";
        }
        if (err.code === "auth/user-disabled") {
            return "User disabled";
        }
        if (err.code === "auth/user-not-found") {
            return "User not found";
        }
        return err.message;
    }

    private _getContinueUrl() {
        const continueUrl = sanitizeRelativeUrl(new URLSearchParams(location.search).get("continue") ?? "/");
        return preserveGA4QueryParams(continueUrl);
    }

    private _initializeAllExperiments = async () => {
        const experimentOverrides = JSON.parse(localStorage.getItem("overrideExperiments") ?? "{}");
        localStorage.setItem("overrideExperiments", JSON.stringify({
            ...experimentOverrides,
            "onboarding_survey": false
        }));
        const experiments = await initializeAllExperiments();
        await this._updateState({ experiments });
    }

    public createNewAccount = async ({ password }: { password: string }) => {
        logger.info(`${loggerPrefix} Creating new account for user ${this._state.email}`);

        const email = this._state.email;

        if (email) {
            const ssoStatus = await this._getSSOStatus();
            if (ssoStatus === "strict" || (ssoStatus === "optional" && !password)) {
                this.setPageMode("ssoRedirect");
                return;
            }
        }

        if (!email && !password) {
            this.setErrorMessage("Email and password required");
            return;
        }
        if (!email) {
            this.setErrorMessage("Email required");
            return;
        }
        if (!password) {
            this.setErrorMessage("Password required");
            return;
        }

        this.setActivePageStatus("submitting");

        try {
            await this._withRegisterPromise(async () => {
                const { user } = await this._auth.createUserWithEmailAndPassword(email, password);
                await this._initializeAllExperiments();
                await this._ensureUserRegistered({ user, provider: "email" });
            });
        } catch (err) {
            logger.error(err, `${loggerPrefix} createNewAccount() failed to create new user ${email}`);
            this.setErrorMessage(this._humanizeFirebaseError(err));
        }
    }

    public signInToAccount = async ({ provider, password, token }: { provider: Provider, password?: string, token?: string }) => {
        const email = this._state.email;
        logger.info(`${loggerPrefix} Signing in to existing account for user ${email} with provider ${provider}`);

        if (email) {
            const ssoStatus = await this._getSSOStatus();
            if (ssoStatus === "strict" || (ssoStatus === "optional" && !password)) {
                this.setPageMode("ssoRedirect");
                return;
            }
        }

        if (provider === "email") {
            if (!password && !email) {
                this.setErrorMessage("Email and password required");
                return;
            }
            if (!email) {
                this.setErrorMessage("Email required");
                return;
            }
            if (!password) {
                this.setErrorMessage("Password required");
                return;
            }

            this.setActivePageStatus("submitting");

            try {
                await this._withRegisterPromise(async () => {
                    const { user } = await this._auth.signInWithEmailAndPassword(email, password);
                    await this._initializeAllExperiments();
                    await this._ensureUserRegistered({ provider, user });
                });
            } catch (err) {
                logger.error(err, `${loggerPrefix} signInToAccount() failed for user ${email} with provider ${provider}`);
                this.setErrorMessage(this._humanizeFirebaseError(err));
            }
        }
        if (provider === "google" && token && token.length > 0) {
            logger.info(`${loggerPrefix} Signing in to Firebase with Google auth credential`);
            try {
                await this.setPageMode("waitForGoogle");

                await this._withRegisterPromise(async () => {
                    const credential = firebase.auth.GoogleAuthProvider.credential(token);
                    const { user } = await this._auth.signInWithCredential(credential);
                    await this._initializeAllExperiments();
                    await this._ensureUserRegistered({ provider: "google", user });
                });
            } catch (err) {
                logger.error(err, `${loggerPrefix} signInToAccount() failed for user ${email} with provider ${provider}`);
                await this.setErrorMessage(this._humanizeFirebaseError(err));
            }
        }
        if (provider === "microsoft" && token && token.length > 0) {
            logger.info(`${loggerPrefix} Signing in to Firebase with Microsoft auth credential`);
            try {
                await this.setPageMode("waitForMicrosoft");

                await this._withRegisterPromise(async () => {
                    const { user } = await this._auth.signInWithCustomToken(token);
                    await this._initializeAllExperiments();
                    await this._ensureUserRegistered({ provider: "microsoft", user });
                });
            } catch (err) {
                logger.error(err, `${loggerPrefix} signInToAccount() failed for user ${email} with provider ${provider}`);
                await this.setErrorMessage(this._humanizeFirebaseError(err));
            }
        }
    }

    public resetPassword = async () => {
        logger.info(`${loggerPrefix} Sending password reset email for user ${this._state.email}`);
        await this.setActivePageStatus("submitting");

        try {
            await emailApi.sendPasswordResetEmail({ email: this._state.email });
            await this.setActivePageStatus("success");
        } catch (err) {
            logger.error(err, `${loggerPrefix} resetPassword() failed for user ${this._state.email}`);
            await this.setErrorMessage(err.message);
        }
    }

    public setEmail = async (email: string) => {
        if (this.currentUser || this._state.readonlyEmail) {
            return;
        }

        await this._updateState({ email });
    }

    private _handleGoogleRedirectResult = async () => {
        const params = new URLSearchParams(window.location.search);

        if (params.has("google_auth_success") && params.get("google_auth_success") === "true") {
            const { idToken } = getGoogleAuthTokens();
            removeQueryParam("google_auth_success");

            if (!idToken) {
                logger.error(new Error(`${loggerPrefix} _handleGoogleRedirectResult`), `${loggerPrefix} _handleGoogleRedirectResult Google auth id token not found in localStorage()`);
                return await this.setErrorMessage("Authentication failed: Unable to retrieve authentication token. Safari privacy protection may be in effect. Please try again or use email/password sign-in.");
            }

            return await this.signInToAccount({ provider: "google", token: idToken });
        }

        if (params.has("google_auth_error")) {
            const errorMessage = params.get("google_auth_error") || "Authentication failed";
            removeQueryParam("google_auth_error");
            return await this.setErrorMessage(errorMessage);
        }
    }

    // Copied from js/core/oauth/googleAuth to reduce dependency loading issues
    private _authenticateWithGoogle(scope: string, continueUrl: string): void {
        window.location.href = `/google/auth?scope=${encodeURIComponent(scope)}&continueUrl=${encodeURIComponent(continueUrl)}`;
    }

    public authenticateWithGoogle = async () => {
        await this.setPageMode("waitForGoogle");

        const scope = "profile email";
        this._authenticateWithGoogle(scope, `${window.location.pathname}${window.location.search}`);
    }

    private _handleMicrosoftRedirectResult = async () => {
        const params = new URLSearchParams(window.location.search);

        if (params.has("microsoft_auth_success") && params.get("microsoft_auth_success") === "true") {
            const { customToken } = getMicrosoftAuthTokens();
            removeQueryParam("microsoft_auth_success");

            if (!customToken) {
                logger.error(new Error(`${loggerPrefix} _handleMicrosoftRedirectResult`), `${loggerPrefix} _handleMicrosoftRedirectResult Microsoft auth custom token not found in localStorage`);
                return await this.setErrorMessage("Authentication failed: Unable to retrieve authentication token. Safari privacy protection may be in effect. Please try again or use email/password sign-in.");
            }

            return await this.signInToAccount({ provider: "microsoft", token: customToken });
        }

        if (params.has("microsoft_auth_error")) {
            const errorMessage = params.get("microsoft_auth_error") || "Authentication failed";
            removeQueryParam("microsoft_auth_error");
            return await this.setErrorMessage(errorMessage);
        }
    }

    private _authenticateWithMicrosoft(scope: string, continueUrl: string): void {
        window.location.href = `/microsoft/auth?scope=${encodeURIComponent(scope)}&continueUrl=${encodeURIComponent(continueUrl)}`;
    }

    public authenticateWithMicrosoft = async () => {
        await this.setPageMode("waitForMicrosoft");
        const scope = "profile email openid";
        this._authenticateWithMicrosoft(scope, `${window.location.pathname}${window.location.search}`);
    }

    public get currentUser() {
        return this._state.currentUser;
    }

    /************************
     Auth - Verification
     ************************/

    private _waitForEmailVerification = async () => {
        const user = this.currentUser;

        if (!user) {
            this.setErrorMessage("No current user available for email verification");
        }

        while (!user.emailVerified) {
            await user.reload();
            if (!user.emailVerified) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }

        await user.getIdToken(true);
        await this._updateState({ currentUser: user });
    }

    public checkVerificationCode = async (code: string) => {
        await this.setActivePageStatus("submitting");
        if (code.length !== 6) {
            return this.setErrorMessage("Verification code must be 6 digits long");
        }

        const res = await Api.verifyEmail.get({ code });
        if (res.verified) {
            await this._waitForEmailVerification();
            Analytics.Auth.VerifyEmail();
            Analytics.SignupFlow.NavForward({ step_name: "May 2026 Verify Email", step_number: 2 });
            return this.setActivePageStatus("success");
        } else {
            if (res.reason === "expired") {
                return this.setErrorMessage("Code expired. Please resend a new code");
            }
            return this.setErrorMessage("Incorrect code. Please try again");
        }
    }

    public resendVerificationEmail = async () => {
        await Api.verifyEmail.post();
        await this.setActivePageStatus("idle");
    }

    public setVerificationCode = async (verificationCode: VerificationCode) => {
        const fullyPopulated = !verificationCode.some(cell => cell === null);

        if (fullyPopulated) {
            const code = verificationCode.join("");

            this.checkVerificationCode(code);
        }

        await this._updateState({
            verificationCode: [...verificationCode] as VerificationCode,
            activePageStatus: fullyPopulated ? "submitting" : "idle",
        });
    }

    public setVerificationCodeCell = async (index: number, value: VerificationCell) => {
        if (index < 0 || index >= this._state.verificationCode.length) {
            return;
        }

        const verificationCode = [...this._state.verificationCode] as VerificationCode;
        verificationCode[index] = value;

        await this.setVerificationCode(verificationCode);
    }

    public resetVerificationCode = async () => {
        await this._updateState({ verificationCode: [null, null, null, null, null, null] });
    }

    public continueAfterVerification = () => {
        const { isNewUser } = this._state;
        if (isNewUser) {
            this.setPageMode("tour");
            return;
        }

        window.location.href = this._getContinueUrl();
    }

    /************************
     Onboarding General
     ************************/

    public setPageLoaded = async () => {
        if (this._state.activePageStatus === "loading") {
            this.setActivePageStatus("idle");
        }
    }

    private setActivePageStatus = async (activePageStatus: ActivePageStatus) => {
        await this._updateState({ activePageStatus });
    }

    private setErrorMessage = async (errorMessage: string) => {
        await this._updateState({ errorMessage, activePageStatus: "error" });
    }

    private _pushParamToUrl(param: string, value: string) {
        try {
            if (!window.history.pushState) {
                return;
            }

            const url = new URL(window.location.href);
            if (url.searchParams.get(param) === value) {
                return;
            }

            url.searchParams.set(param, value);
            window.history.pushState({ path: url.href }, "", url.href);
        } catch (err) {
            logger.warn(`${loggerPrefix} _pushParamToUrl failed: ${err.message}`);
        }
    }

    public setPageMode = async (pageMode: PageMode, showPageModeInUrl: boolean = false) => {
        await this._updateState({ pageMode, activePageStatus: "loading", errorMessage: "" });
        if (showPageModeInUrl) {
            this._pushParamToUrl("pageMode", pageMode);
        }
    }

    public get tourPage() {
        return this._state.tourPage;
    }

    public setTourPage = async (tourPage: number) => {
        const currentTourPage = this.tourPage || 1;
        Analytics.SignupFlowMay2026.NavForward({ tourPage: currentTourPage });
        await this._updateState({ tourPage });
    }

    public answerSurveyQuestion({ id }: TourSurveyQuestion) {
        const response = id as PossibleAnswers;
        Analytics.SignupFlowMay2026.QuestionAnswered({ response });
        Analytics.SignupFlowMay2026.NavForward({ tourPage: this.tourPage });

        const { experiments } = this._state;
        if (experiments?.pro_plan_preview?.enabled) {
            window.location.href = preserveGA4QueryParams("/");
            return;
        }

        this.setPageMode("pricing", true);
    }

    public startCheckout = async (plan: string, interval: string) => {
        window.location.href = preserveGA4QueryParams(`/checkout?plan=${plan}&billingTerm=${interval}&variant=true`);
    }

    public dispose() {
        this._disposed = true;
        if (this._unsubscribeAuthState) {
            this._unsubscribeAuthState();
            this._unsubscribeAuthState = null;
        }
    }
}

const authController = new AuthController();

// for lazy import and debug
app.authController = authController;

//@ts-ignore
export default authController;
